/**
 * The eval bar at the LEFT EDGE of the board frame (fix D / R4), drawn by the
 * repo's own `WinningBar` (the game-review bar) rather than a second one.
 *
 * It is ABSOLUTELY POSITIONED outside the Stage column: the column's width is the
 * board frame's, and the two player rows stretch to it (R6.3), so a bar in flow
 * would have made the rows 26 px wider than the board. The overhang lands in the
 * page padding / the row's centring slack.
 *
 * WIDTH: `WinningBar` hardcodes 20 px on the desktop (its own
 * `winningBarContainer`), so the slot is 20 and not R4's nominal 10 — the
 * component is reused as-is rather than forked.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

/**
 * `WinningBar` is required on FIRST RENDER, not imported: the `ChessAnalysis`
 * closure reaches `@/navigators/StackConstants` (every screen, this one
 * included) and a static import made a webpack module cycle through the rail
 * (crash 2026-10-05). The bar only mounts while analysis is on.
 */
type WinningBarProps = {
  whiteWinRatio: number;
  blackWinRatio: number;
  reverse?: boolean;
};
let WinningBarCached: React.ComponentType<WinningBarProps> | null = null;
function WinningBar(props: WinningBarProps) {
  if (!WinningBarCached) {
    WinningBarCached =
      require('@/Screens/ChessAnalysis/component/WinningBar').default;
  }
  const Bar = WinningBarCached as React.ComponentType<WinningBarProps>;
  return <Bar {...props} />;
}

import {EvalBarModel} from './model';

/** `WinningBar`'s own desktop width, plus the gap to the frame */
const BAR_WIDTH = 20;
const BAR_GAP = 6;

export default React.memo(function EvalBarView({
  topRatio,
  bottomRatio,
  reverse,
  boardSize,
  offset,
}: EvalBarModel) {
  return (
    <View
      testID="v2-eval-bar"
      pointerEvents="none"
      style={[styles.slot, {height: boardSize, top: offset}]}>
      <WinningBar
        whiteWinRatio={topRatio}
        blackWinRatio={bottomRatio}
        reverse={reverse}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  slot: {
    position: 'absolute',
    width: BAR_WIDTH,
    left: -(BAR_WIDTH + BAR_GAP),
  },
});
